import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import Select from 'react-select';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import clone from 'lodash.clone';
import orderBy from 'lodash.orderby';
import cx from 'classnames';
import { selectTheme, selectStyles } from '../../shared/react-select-theme';
import { mapValueToOptions, consultOption } from '../../shared/lib';
import { HIDE_QUESTION } from '../../shared/reducer';
import { useStateValue } from '../../shared/state';

import styles from './styles.FormFieldSelectConsults';

/**
 * Map Consults array to ID-keyed Object
 * @example From: [{},{},{}] To: {a1: {}, b2: {}, c3: {}}
 * @param {Array} consults
 * @return {Object}
 */
const prepareConsults = (consults = []) => {
  const map = {};
  consults.forEach((consult) => (map[consult.id] = consult));
  return map;
};

/**
 * Format list of options for ReactSelect
 * @param {Object} consults
 * @returns {Array}
 */
const prepareOptions = (consults = {}) => {
  // Sort consults object in descending order by date i.e. latest first
  // Returns an array, from which we extract the IDs
  // which are required in the reduce operation
  return orderBy(consults, 'scheduledDate', 'desc')
    .map((item) => item.id)
    .reduce((options, consultId) => {
      // 1. Create New Option from Consult
      let newConsultOption = consultOption({
        consult: consults[consultId],
      });
      // If it's not empty, include it in the set
      newConsultOption && options.push(newConsultOption);
      // Return Options
      return options;
    }, []);
};

/**
 * Clones value from options into Consult related fields
 * @param {String} name
 * @param {Object} source
 * @param {Object} values
 * @param {Function} setFieldValue
 * @param {Boolean}
 */
const copyValue = (name, source, values, setFieldValue) => {
  if (
    values.hasOwnProperty(name) && // 1. Form values has value key
    isEmpty(values[name]) && // 2. Current value is empty (No Overwrite)
    !isEmpty(source[name]) // 3. Source value is not empty
  ) {
    setFieldValue(name, clone(source[name]));
    return true;
  }

  return false;
};

const cloneTopicsFromRelatedConsult = (
  relatedProSessionSummary,
  values,
  setFieldValue
) => {
  if (!relatedProSessionSummary) {
    return false;
  }

  const copiedThemesPrimaryTopic = copyValue(
    'themesPrimaryTopic',
    relatedProSessionSummary,
    values,
    setFieldValue
  );

  const copiedThemesAdditionalTopics = copyValue(
    'themesAdditionalTopics',
    relatedProSessionSummary,
    values,
    setFieldValue
  );

  return copiedThemesPrimaryTopic || copiedThemesAdditionalTopics;
};

export default function FormFieldSelectConsults({
  field: { name, value, onChange, onBlur },
  form: { touched, errors, setFieldTouched, setFieldValue, values },
  question,
  context,
  ...props
}) {
  const consults = prepareConsults(context.employee.completedConsults);
  const options = prepareOptions(consults);
  const localValue = mapValueToOptions(value, options, true);
  const [clonedTopics, setClonedTopics] = useState(null); // null | true | false
  const [flashDismissed, setFlashDismissed] = useState(false);
  const [{}, dispatch] = useStateValue(); // eslint-disable-line no-empty-pattern

  // Hide this question if there are no choices
  useEffect(() => {
    if (isEmpty(options)) {
      dispatch({
        type: HIDE_QUESTION,
        question: question,
      });
    }
  }, []);

  // TODO: Limit this effect to ProSessionSummary
  useEffect(() => {
    if (clonedTopics === null && localValue.length) {
      let result = cloneTopicsFromRelatedConsult(
        get(consults, `${localValue[0].value}.proSessionSummary`),
        values,
        setFieldValue
      );

      setClonedTopics(!!result);
    }
  }, [localValue]);

  useEffect(() => {
    if (clonedTopics === true) {
      setTimeout(setFlashDismissed, 5000, true);
    }
  }, [clonedTopics]);

  return (
    <div className={styles.consultSelect}>
      <Field
        component={Select}
        theme={selectTheme}
        styles={selectStyles}
        name={name}
        id={name}
        options={options}
        onBlur={() => setFieldTouched(name, true)}
        onChange={(selected) => {
          setFieldValue(
            name,
            selected && selected.map((option) => option.value)
          );
        }}
        isMulti={true}
        value={localValue}
      />
      {clonedTopics && (
        <div
          className={cx(styles.flashNotice, {
            [styles.flashDismissed]: flashDismissed,
          })}
          onClick={() => {
            setFlashDismissed(true);
          }}
        >
          Related topics from a prior session were automatically copied below.
          You can change these if they no longer apply for this session.
        </div>
      )}
    </div>
  );
}

FormFieldSelectConsults.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
};
